/**
 * FIXTURE FORM
 * 
 * Wrapper component for a single fixture that combines:
 * - Fixture label
 * - Condition selector (N/A | Keep | Replace | Add New)
 * - Detail fields (when not N/A)
 * - Notes field
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import FixtureConditionSelector from './FixtureConditionSelector';
import FixtureDetails from './FixtureDetails';

const FixtureForm = ({ fixture, value = {}, onChange, disabled = false }) => {
  const [isExpanded, setIsExpanded] = useState(
    value.condition && value.condition !== 'na'
  );
  
  const handleConditionChange = (condition) => {
    onChange({
      ...value,
      condition,
      // Reset details if changing to N/A
      details: condition === 'na' ? {} : (value.details || {}),
    });
    // Auto-expand when selecting something other than N/A
    if (condition !== 'na') {
      setIsExpanded(true);
    }
  };
  
  const handleDetailChange = (key, detailValue) => {
    onChange({
      ...value,
      details: {
        ...(value.details || {}),
        [key]: detailValue,
      },
    });
  };
  
  const handleNotesChange = (notes) => {
    onChange({
      ...value,
      notes,
    });
  };

  const condition = value.condition || 'na';
  const isActive = condition !== 'na';
  
  // Determine border color based on condition
  let borderColor = 'border-slate-700';
  if (condition === 'exists_replace') {
    borderColor = 'border-amber-500/40';
  } else if (condition === 'add_new') {
    borderColor = 'border-emerald-500/40';
  } else if (condition === 'exists_keep') {
    borderColor = 'border-blue-500/40';
  }

  return (
    <div className={`border rounded bg-slate-900/50 ${borderColor} transition-all`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-slate-200 text-sm">{fixture.label}</span>
          {isActive && (
            <span className={`
              text-xs px-2 py-0.5 rounded
              ${condition === 'exists_replace' ? 'bg-amber-500/20 text-amber-400' : ''}
              ${condition === 'add_new' ? 'bg-emerald-500/20 text-emerald-400' : ''}
              ${condition === 'exists_keep' ? 'bg-blue-500/20 text-blue-400' : ''}
            `}>
              {condition === 'exists_replace' ? 'Replace' : ''}
              {condition === 'add_new' ? 'Add New' : ''}
              {condition === 'exists_keep' ? 'Keep' : ''}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        )}
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-700/50 pt-3">
          {/* Condition Selector */}
          <FixtureConditionSelector
            value={condition}
            onChange={handleConditionChange}
            disabled={disabled}
          />
          
          {/* Detail Fields (only if not N/A) */}
          {isActive && fixture.details && Object.keys(fixture.details).length > 0 && (
            <FixtureDetails
              fixture={fixture}
              details={value.details || {}}
              onChange={handleDetailChange}
              disabled={disabled}
            />
          )}
          
          {/* Notes (only if not N/A) */}
          {isActive && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">Notes</label>
              <textarea
                value={value.notes || ''}
                onChange={(e) => handleNotesChange(e.target.value)}
                disabled={disabled}
                rows={2}
                placeholder="Any specific notes about this fixture..."
                className="w-full px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FixtureForm;
